/**
 * `WindowFrame` — the frame a combat window ({@link CombatWindow}) sits in: the padded
 * `.gms-window` container that projects the dithered "desk mat" glow, the bordered
 * `.gms-card` (drop shadow + 2px ink border), a draggable title bar (glyph + name +
 * optional `controls` + close), and eight perimeter resize handles.
 *
 * The owner ({@link useCombatWindows}) holds `pos`/`size` plus a z-order slot; this component
 * runs the drag/resize gestures and reports back through `onPos`/`onSize`, and lifts itself to
 * the front (`onFocus`) on any mousedown inside it.
 *
 * **Drag lifecycle.** `onDragStart`/`onDragEnd` fire at the start/end of a *title-bar* drag (never
 * on resize) so the registry can drive snap-to-edge + the dock-zone overlay off the live gesture.
 * `windowId` stamps the card with `data-gms-window` so the registry can read every open window's
 * rect off the DOM as a snap/dock neighbour.
 *
 * **Full-perimeter resize.** Eight handles (N/S/E/W edges + 4 corners). The per-handle
 * delta→geometry math is the pure {@link resizeGeometry} — the top/left edges *move the origin*
 * (x/y), not just the size, pinning the opposite edge under the min-size clamp. When the owner
 * supplies `onResizeStart`/`onResize`/`onResizeEnd`, the gesture reports the full geometry up
 * through `onResize` so the owner can snap the resized edge ({@link resolveResizeSnap}); without
 * `onResize` the geometry is applied through `onPos`/`onSize`.
 */
import { faXmark, type IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { motion } from "motion/react";
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { Icon } from "../../../lib/Icon";
import { prefersReducedMotion } from "../../../lib/prefersReducedMotion";
import { tipAttrs } from "../../../lib/tooltip";
import {
  TOP_INSET,
  PAD_LEFT,
  PAD_TOP,
  clamp,
  resizeGeometry,
  type PxGeometry,
  type ResizeHandle,
  type WindowPos,
  type WindowSize,
} from "../windowGeometry";

// Window open/close motion — a brief "power-on" snap (scale up + fade in) on mount and the
// reverse on unmount. Exit only plays when the window sits under an <AnimatePresence> that
// holds the unmount until the animation completes (COMBAT's window host). Kept
// short and subtle so the terminal stays crisp rather than bouncy.
const WIN_INITIAL = { opacity: 0, scale: 0.94 } as const;
const WIN_ENTER = { opacity: 1, scale: 1 } as const;
const WIN_EXIT = { opacity: 0, scale: 0.96 } as const;
const WIN_TRANSITION = { duration: 0.16, ease: "easeOut" } as const;

// Under `prefers-reduced-motion: reduce` there is no motion at all, matching every CSS animation in
// the app (`animation: none`): the window mounts with `initial={false}`, so its first frame paints
// at rest, and a zero-duration transition lets its exit remove it within a frame. `motion`'s own
// `MotionConfig reducedMotion="user"` is not this: it still paints `initial` for one frame, keeps
// the 160 ms fade, and leaves a closing window hit-testable while it fades. The query is read on
// every render (`prefersReducedMotion` calls `matchMedia` each time) rather than through
// `useReducedMotion`, which freezes its value at mount, so an OS toggle applies from the next
// open; an exiting window keeps the transition it last rendered with. The e2e harness emulates
// the query by default, so flows take this path unless `E2E_MOTION=1`.
const WIN_SNAP = { duration: 0 } as const;

// `motion`'s own bookkeeping already treats `initial` as mount-only (a re-render that leaves
// `animate`'s resolved values unchanged is a no-op, not a replay) — but that safety is implicit,
// riding on `WIN_INITIAL`/`WIN_ENTER` staying stable module-level references forever. A refocus
// (z-order bump) or any other prop churn re-renders this component, and it must NEVER re-play the
// "power-on" fade — only a genuine mount (the window opening) does. Pin that down explicitly with a
// mount-tracking ref rather than depending on the constants above never changing shape: `initial`
// is `WIN_INITIAL` on this component instance's first render only, then hard-pinned to `false` for
// every render after, however many times it re-renders while it stays mounted.

/**
 * A press-drag gesture. `onDelta(dx, dy)` fires with the cursor offset from where the
 * press began; the caller clamps and applies it against a base captured at press time.
 * Listeners live on `document` (so a fast drag that outruns the handle still tracks) and
 * are attached only while the gesture is live. The optional `onEnd` fires once on mouseup.
 */
function useDragGesture(onDelta: (dx: number, dy: number) => void, onEnd?: () => void) {
  const [active, setActive] = useState(false);
  const start = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (!active) return;
    const onMove = (e: MouseEvent) => onDelta(e.clientX - start.current.x, e.clientY - start.current.y);
    const onUp = () => {
      setActive(false);
      onEnd?.();
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [active, onDelta, onEnd]);
  return (e: ReactMouseEvent) => {
    start.current = { x: e.clientX, y: e.clientY };
    setActive(true);
    e.preventDefault();
  };
}

export function WindowFrame(props: {
  /** The title bar's icon (a Font Awesome solid icon). */
  glyph: IconDefinition;
  title: string;
  pos: WindowPos;
  onPos: (p: WindowPos) => void;
  size: WindowSize;
  onSize: (s: WindowSize) => void;
  z: number;
  onFocus: () => void;
  /** Optional: keyboard focus moving into the card (React `onFocus` bubbles like `focusin`). */
  onFocusIn?: () => void;
  onClose: () => void;
  /** The key that also closes it, named in the close button's tooltip (a combat window's `Esc`). */
  closeKey?: string;
  /** Optional drag-lifecycle hooks (title-bar drag only) — see the file header. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Optional resize-lifecycle hooks (any of the eight handles) — see the file header. */
  onResizeStart?: (handle: ResizeHandle) => void;
  /** When supplied, the resize gesture reports its full geometry here (manager snaps it). */
  onResize?: (geo: { pos: WindowPos; size: WindowSize }) => void;
  onResizeEnd?: () => void;
  /** Optional window id, stamped as `data-gms-window` so the manager can read this rect. */
  windowId?: string;
  minW?: number;
  minH?: number;
  controls?: ReactNode;
  children: ReactNode;
}) {
  const { glyph, title, pos, onPos, size, onSize, z, onFocus, onFocusIn, onClose, closeKey, onDragStart, onDragEnd, onResizeStart, onResize, onResizeEnd, windowId, controls, children } = props;
  const minW = props.minW ?? 380;
  const minH = props.minH ?? 180;

  // See the WIN_INITIAL/WIN_ENTER comment above: `false` on every render after the first, so a
  // refocus/z-order re-render can never replay the mount fade (only used on THIS render; flipped
  // to `true` below so it's `false` starting the very next render).
  const reducedMotion = prefersReducedMotion();
  const hasMountedRef = useRef(false);
  const initial = hasMountedRef.current || reducedMotion ? false : WIN_INITIAL;
  hasMountedRef.current = true;

  const posBase = useRef({ x: 0, y: 0 });
  const onTitleDown = useDragGesture((dx, dy) => {
    onPos({
      x: clamp(posBase.current.x + dx, -PAD_LEFT, window.innerWidth - 128),
      y: clamp(posBase.current.y + dy, TOP_INSET - PAD_TOP, window.innerHeight - 108),
    });
  }, onDragEnd);
  const beginDrag = (e: ReactMouseEvent) => {
    posBase.current = { x: pos.x, y: pos.y };
    onDragStart?.();
    onTitleDown(e);
  };

  // Resize gesture — one document-level drag shared by all eight handles. The pressed handle
  // and the geometry at press time are captured in refs so a single `useDragGesture` (hooks
  // can't loop) serves every grip; `resizeGeometry` turns the cursor delta into the new
  // geometry (top/left handles move the origin). A manager consumes the full geometry via
  // `onResize` (and snaps it); a bare caller gets it applied straight through `onPos`/`onSize`.
  const resizeBase = useRef<PxGeometry>({ pos: { x: 0, y: 0 }, size: { w: 0, h: 0 } });
  const handleRef = useRef<ResizeHandle>("se");
  const onResizeDown = useDragGesture((dx, dy) => {
    const next = resizeGeometry({ handle: handleRef.current, base: resizeBase.current, delta: { dx, dy }, min: { w: minW, h: minH } });
    if (onResize) {
      onResize(next);
    } else {
      onPos(next.pos);
      onSize(next.size);
    }
  }, onResizeEnd);
  const beginResize = (handle: ResizeHandle) => (e: ReactMouseEvent) => {
    handleRef.current = handle;
    resizeBase.current = { pos: { x: pos.x, y: pos.y }, size: { w: size.w, h: size.h } };
    onResizeStart?.(handle);
    onResizeDown(e);
  };

  return (
    <motion.div
      className="gms-window"
      // `--gms-win-h` publishes the live (resize-tracking) `size.h` to the subtree, and
      // `gms-desktop.css` spends it as `.gms-card`'s `max-height` — so the CARD, the box every
      // snap/dock rectangle is computed against, can never render taller than the geometry the
      // manager stored for it. There's still no inline height (a window stays content-driven:
      // a short body makes a short card); the cap only bites at the top end, where the card's
      // flex column lets `.gms-content` shrink and scroll instead of the card overflowing.
      // Owners' inline `maxHeight`/`height` on their own content hosts are non-binding upper
      // bounds under it. See the `.gms-card` block comment for why the cap can't live on the
      // content host: capping the content adds the title bar / tab strip / borders on top.
      style={{ left: pos.x, top: pos.y, width: size.w + PAD_LEFT * 2, zIndex: z, "--gms-win-h": `${size.h}px` } as CSSProperties}
      initial={initial}
      animate={WIN_ENTER}
      exit={WIN_EXIT}
      transition={reducedMotion ? WIN_SNAP : WIN_TRANSITION}
    >
      <div className="gms-deskmat" />
      {/* mousedown anywhere on the card lifts it to the front */}
      <div className="gms-card" data-gms-window={windowId} onMouseDown={onFocus} onFocus={onFocusIn}>
        {/* The drag bar itself is non-interactive chrome (no button role), so the delegated UI
            sfx never fires on grabbing/hovering it — only its real buttons (the ✕ close, a
            window's `controls`) sound, which is intended (see `uiFeedback.ts`). */}
        <div className="gms-titlebar" onMouseDown={beginDrag}>
          <Icon icon={glyph} className="gms-titlebar-icon" />
          <span className="gms-titlebar-name">{title}</span>
          <div className="gms-titlebar-fill" />
          {controls}
          <button type="button" className="gms-close" aria-label="Close window" {...tipAttrs({ label: "Close window", key: closeKey })} onClick={onClose}>
            <Icon icon={faXmark} />
          </button>
        </div>

        {children}

        {/* Eight perimeter resize handles (N/S/E/W edges + 4 corners). The SE corner keeps the
            etched-diagonal grip; the rest are invisible grab strips straddling the card border. */}
        <button type="button" className="gms-rz gms-rz-n" aria-label="Resize from top" onMouseDown={beginResize("n")} />
        <button type="button" className="gms-rz gms-rz-s" aria-label="Resize from bottom" onMouseDown={beginResize("s")} />
        <button type="button" className="gms-rz gms-rz-e" aria-label="Resize from right" onMouseDown={beginResize("e")} />
        <button type="button" className="gms-rz gms-rz-w" aria-label="Resize from left" onMouseDown={beginResize("w")} />
        <button type="button" className="gms-rz gms-rz-ne" aria-label="Resize from top-right" onMouseDown={beginResize("ne")} />
        <button type="button" className="gms-rz gms-rz-nw" aria-label="Resize from top-left" onMouseDown={beginResize("nw")} />
        <button type="button" className="gms-rz gms-rz-sw" aria-label="Resize from bottom-left" onMouseDown={beginResize("sw")} />
        <button type="button" className="gms-rz gms-resize" aria-label="Resize from bottom-right" onMouseDown={beginResize("se")} />
      </div>
    </motion.div>
  );
}
